import { Injectable, Injector } from '@angular/core';
import { BehaviorSubject, Observable, of, catchError, map, shareReplay, switchMap, firstValueFrom } from 'rxjs';
import { AuthConfig, SYNC_DATABASE_CONFIG, TABLE_STORES } from './indexed-db.service';
import { DataSyncService } from './data-sync.service';
import { IndexedDbService } from './indexed-db.service';
import { ConnectionService, ConnectivityStatus } from './connection.service';
import { MobileClient } from '../api/mobile-client';
import { MobileLoginRequest } from '../api/mobile-login-request';
import { TokenRefreshRequest } from '../api/token-refresh-request';
import { AuthUserInfo } from '../api/auth-user-info';
import { TokenResponse } from '../api/token-response';

export enum AuthState {
  Init,
  LoggedIn,
  LoggedOut
}

export interface AuthTokens {
  AccessToken: string;
  RefreshToken: string;
  ExpiresIn: number; // seconds
}

// Use AuthUserInfo from generated API client
export { AuthUserInfo as UserInfo } from '../api/auth-user-info';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private _user?: AuthUserInfo;
  private _authStateSubject: BehaviorSubject<AuthState> = new BehaviorSubject<AuthState>(AuthState.Init);
  private _authCheck$: Observable<AuthState> | null = null;
  private lastProfileSync: Date | null = null;
  private static readonly PROFILE_STALE_MS = 30 * 60 * 1000;

  public readonly authState$: Observable<AuthState> = this._authStateSubject.asObservable();
  public UserSubject = new BehaviorSubject<AuthUserInfo | undefined>(undefined);

  private authConfig?: AuthConfig;

  constructor(
    private mobileClient: MobileClient,
    private dataSync: DataSyncService,
    private indexedDb: IndexedDbService,
    private connectionService: ConnectionService,
    private injector: Injector,
  ) {
    this.authState$.subscribe((state: AuthState) => {
      if (state === AuthState.LoggedOut) {
        this.authConfig = undefined;
        this.lastProfileSync = null;
        this.setAuthConfigToDb(undefined).catch(() => { });
      }
    });

    this.connectionService.connectivityStatus$.subscribe(async (status: ConnectivityStatus) => {
      if (status.isOnline && this._authStateSubject.getValue() === AuthState.LoggedIn) {
        await this.refreshUserProfile(true);
      }
    });
  }

  public async init(): Promise<void> {
    // Prevent multiple initializations - only run if in Init state
    if (this._authStateSubject.getValue() !== AuthState.Init) {
      return;
    }

    this.authConfig = await this.getAuthConfigFromDb();

    if (this.authConfig) {
      this._setAuthState(AuthState.LoggedIn);

      // Populate User from cached userInfo
      if (this.authConfig.userInfo) {
        this.User = this.authConfig.userInfo;
      }
    } else {
      this._setAuthState(AuthState.LoggedOut);
    }
  }

  public get User() {
    return this._user;
  }
  public set User(user: AuthUserInfo | undefined) {
    if (this._user !== user) {
      this._user = user;
      this.UserSubject.next(user);
    }
  }

  public get isBellEmployee(): boolean {
    return this._user?.IsBellEmployee ?? false;
  }

  public get isSignedIn() : boolean {
    return !!this.authConfig && !!this.authConfig.accessToken;
  }

  public getAccessToken(): string {
    return this.authConfig?.accessToken || '';
  }

  public getRefreshToken(): string {
    return this.authConfig?.refreshToken || '';
  }

  public onUserChanged(): Observable<AuthUserInfo | undefined> {
    return this.UserSubject.asObservable();
  }

  private _setAuthState(state: AuthState): void {
    const currentState = this._authStateSubject.getValue();
    if (currentState !== state) {
      this._authStateSubject.next(state);
    }
  }

  private _checkAuthenticationOnce(): Observable<AuthState> {
    if (this._authCheck$) {
      return this._authCheck$;
    }

    this._authCheck$ = of(null).pipe(
      switchMap(() => this.getUserInfo()),
      map(userInfo => {
        if (userInfo && userInfo.UserName) {
          this._setAuthState(AuthState.LoggedIn);
          return AuthState.LoggedIn;
        } else {
          this._setAuthState(AuthState.LoggedOut);
          return AuthState.LoggedOut;
        }
      }),
      catchError(() => {
        this._setAuthState(AuthState.LoggedOut);
        return of(AuthState.LoggedOut);
      }),
      shareReplay(1)
    );

    return this._authCheck$;
  }

  public async getUserInfo(): Promise<AuthUserInfo | undefined> {
    try {
      if (this.authConfig?.userInfo) {
        return this.authConfig.userInfo;
      }
      const response = await firstValueFrom(this.mobileClient.userInfo());
      if (response?.Data && this.authConfig) {
        this.authConfig!.userInfo = response.Data as AuthUserInfo;
        await this.setAuthConfigToDb(this.authConfig);
        this.lastProfileSync = new Date();
      }
      return response?.Data || undefined;
    } catch {
      return undefined;
    }
  }

  /**
   * Fetches fresh profile data from the server, bypassing the local cache.
   * @param force If true, skips the staleness check and always fetches.
   */
  public async refreshUserProfile(force = false): Promise<AuthUserInfo | undefined> {
    if (!navigator.onLine || !this.authConfig?.accessToken) {
      return this.authConfig?.userInfo;
    }

    if (!force && this.lastProfileSync) {
      const elapsed = Date.now() - this.lastProfileSync.getTime();
      if (elapsed < UserService.PROFILE_STALE_MS) {
        return this.authConfig?.userInfo;
      }
    }

    try {
      const response = await firstValueFrom(this.mobileClient.userInfo());
      if (response?.Data && this.authConfig) {
        this.authConfig.userInfo = response.Data as AuthUserInfo;
        await this.setAuthConfigToDb(this.authConfig);
        this.User = response.Data as AuthUserInfo;
        this.lastProfileSync = new Date();
      }
      return response?.Data || undefined;
    } catch (error) {
      console.warn('Profile refresh failed, using cached data:', error);
      return this.authConfig?.userInfo;
    }
  }

  public user(): Observable<AuthUserInfo | null> {
    return this._checkAuthenticationOnce().pipe(
      switchMap(authState => {
        if (authState === AuthState.LoggedIn) {
          return of(this.User ?? null);
        }
        return of(null);
      }),
      catchError(() => of(null))
    );
  }

  // Login using phone number and password (and optional 2FA)
  public async signIn(phoneNumber: string, password: string, twoFactorCode?: string): Promise<true | '2fa' | false> {
    try {
      const loginRequest = new MobileLoginRequest({
        PhoneNumber: phoneNumber,
        Password: password,
        TwoFactorCode: twoFactorCode
      });

      const response = await this.mobileClient.login(loginRequest).toPromise();

      if (response?.Data?.AccessToken) {
        const tokenData = response.Data;
        const newAuthConfig: AuthConfig = {
          accessToken: tokenData.AccessToken,
          refreshToken: tokenData.RefreshToken || '',
          expiresAt: Date.now() + tokenData.ExpiresIn * 1000
        };
        this.authConfig = newAuthConfig;
        await this.setAuthConfigToDb(newAuthConfig);

        this._setAuthState(AuthState.LoggedIn);
        this._authCheck$ = null;
        const userInfo = await this.getUserInfo();
        if (userInfo) {
          this.authConfig.userInfo = userInfo;
          await this.setAuthConfigToDb(this.authConfig);
          this.lastProfileSync = new Date();
        }
        this.User = userInfo;
        this.dataSync.syncStore(TABLE_STORES['customers'].storeName);
        return true;
      }
      throw new Error('No access token');
    } catch (error: any) {
      // Check for 2FA required or invalid 2FA code
      if (error && error.status === 401 && error.error && typeof error.error === 'object') {
        const detail = error.error.detail || error.error.title || '';
        if (detail === 'RequiresTwoFactor') {
          return '2fa';
        }
        if (detail === 'InvalidTwoFactorCode') {
          // Still in 2fa flow, but code was wrong
          return '2fa';
        }
      }
      this._setAuthState(AuthState.LoggedOut);
      this._authCheck$ = null;
      this.User = undefined;
      this.authConfig = undefined;
      await this.setAuthConfigToDb(undefined);
      return false;
    }
  }

  // Logout
  public async signOut(): Promise<boolean> {
    try {
      // Lazy-load DraftService to avoid circular dependency
      const { DraftService } = await import('./draft.service');
      const draftService = this.injector.get(DraftService);
      await draftService.clearUserDrafts();
    } catch {
      // Draft cleanup is best-effort; don't block logout
    }

    try {
      if (this.authConfig?.refreshToken) {
        const logoutRequest = new TokenRefreshRequest({
          RefreshToken: this.authConfig.refreshToken
        });
        await this.mobileClient.logout(logoutRequest).toPromise();
      }
      this._setAuthState(AuthState.LoggedOut);
      this._authCheck$ = null;
      this.User = undefined;
      this.authConfig = undefined;
      await this.setAuthConfigToDb(undefined);
      return true;
    } catch {
      return false;
    }
  }

  // Refresh JWT token using refresh token
  public async refreshToken(): Promise<boolean> {
    if (!this.authConfig?.refreshToken) return false;
    try {
      const refreshRequest = new TokenRefreshRequest({
        RefreshToken: this.authConfig.refreshToken
      });
      const response = await firstValueFrom(this.mobileClient.refresh(refreshRequest));
      if (response?.Data?.AccessToken) {
        const tokenData = response.Data;
        const newAuthConfig: AuthConfig = {
          accessToken: tokenData.AccessToken,
          refreshToken: tokenData.RefreshToken || this.authConfig.refreshToken,
          expiresAt: Date.now() + tokenData.ExpiresIn * 1000,
          userInfo: this.authConfig.userInfo
        };
        this.authConfig = newAuthConfig;
        await this.setAuthConfigToDb(newAuthConfig);
        return true;
      }
      throw new Error('No access token');
    } catch {
      this._setAuthState(AuthState.LoggedOut);
      this.authConfig = undefined;
      await this.setAuthConfigToDb(undefined);
      return false;
    }
  }

  // Helper to ensure token is valid before making authenticated requests
  public async ensureValidToken(): Promise<boolean> {
    if (!this.authConfig) return false;
    if (Date.now() > this.authConfig.expiresAt - 600000) { // refresh 1 min before expiry
      return await this.refreshToken();
    }
    return true;
  }

  public reinit(): Observable<AuthState> {
    this._authCheck$ = null;
    this._setAuthState(AuthState.Init);
    return this._checkAuthenticationOnce();
  }

  public isSignedInObservable(): Observable<boolean> {
    const currentState = this._authStateSubject.getValue();
    if (currentState === AuthState.LoggedIn) {
      return of(true);
    } else if (currentState === AuthState.LoggedOut) {
      return of(false);
    }
    return this._checkAuthenticationOnce().pipe(
      map(authState => authState === AuthState.LoggedIn),
      catchError(() => of(false))
    );
  }

  public IsInRole(role: string): boolean {
    if (!this.User || !this.User.Roles) {
      return false;
    }
    return this.User.Roles.includes(role);
  }

  // --- IndexedDB AuthConfig persistence methods ---

  private async getAuthConfigFromDb(): Promise<AuthConfig | undefined> {
    try {
      const config = await this.indexedDb.get<AuthConfig>(SYNC_DATABASE_CONFIG.stores.auth, 'default');
      return config ?? undefined;
    } catch (error) {
      console.error('Failed to get auth config from DB:', error);
      return undefined;
    }
  }

  private async setAuthConfigToDb(config?: AuthConfig): Promise<void> {
    try {
      if (config) {
        await this.indexedDb.put(SYNC_DATABASE_CONFIG.stores.auth, config, 'default');
      } else {
        // Clear auth config
        await this.indexedDb.delete(SYNC_DATABASE_CONFIG.stores.auth, 'default');
      }
    } catch (error) {
      console.error('Failed to set auth config to DB:', error);
      throw error;
    }
  }
}
